/** How a state reads: good, needs a look, broken, or nothing to say. */
export type Tone = 'success' | 'warning' | 'danger' | 'muted';

/**
 * Class names for tones and surfaces, kept out of the component files so each of those exports only
 * components (Fast Refresh discards a module's state when it also exports anything else).
 *
 * ## The two themes are tuned separately, not mirrored
 *
 * A tint that glows against a near-black panel is a smudge on white, and a hairline that is barely
 * there in the dark is invisible in the light. So the unprefixed classes below are chosen for the
 * light theme — firm neutral slate hairlines, a layered shadow, deeper status tints — and the
 * `dark:` ones for the dark theme, where separation comes from the surface itself.
 */

export type SurfaceTone = Tone | 'primary';

export const toneText: Record<SurfaceTone, string> = {
  success: 'text-success',
  warning: 'text-warning',
  danger: 'text-danger',
  muted: 'text-muted-foreground',
  primary: 'text-primary',
};

/** A tinted wash with no outline, for glyphs, pills and icon tiles. */
export const toneTint: Record<SurfaceTone, string> = {
  success: 'bg-success/[0.12] text-success dark:bg-success/10',
  warning: 'bg-warning/[0.12] text-warning dark:bg-warning/10',
  danger: 'bg-danger/[0.10] text-danger dark:bg-danger/10',
  muted: 'bg-slate-900/[0.06] text-slate-600 dark:bg-white/[0.06] dark:text-muted-foreground',
  primary: 'bg-primary/[0.10] text-primary dark:bg-primary/10',
};

/** The wash behind an inline message. No border: the tint is enough to set it apart. */
export const toneWash: Record<SurfaceTone, string> = {
  success: 'bg-success/[0.06] dark:bg-success/[0.07]',
  warning: 'bg-warning/[0.07] dark:bg-warning/[0.08]',
  danger: 'bg-danger/[0.06] dark:bg-danger/[0.08]',
  muted: 'bg-slate-900/[0.035] dark:bg-white/[0.04]',
  primary: 'bg-primary/[0.06] dark:bg-primary/[0.07]',
};

/**
 * A content surface on this page.
 *
 * ## Why not the shared Card
 *
 * `Card` separates itself with a border in the theme's border colour and a blue glow, which is the
 * right weight for a lone panel and too much for a page made of a dozen of them: in the dark theme
 * the page read as a grid of blue outlines. Here separation comes from the surface — in the light
 * theme a white card with a firm neutral hairline and three layers of shadow (a contact shadow, a
 * short ambient one and a long soft one, which is what reads as depth on a light page), in the dark
 * theme a lighter surface with a faint top highlight and a deep, soft shadow.
 */
export const panel =
  'rounded-xl bg-card text-card-foreground ring-1 ring-slate-900/[0.1] ' +
  'shadow-[0_1px_2px_rgb(16_24_40/0.06),0_4px_10px_-2px_rgb(16_24_40/0.07),0_16px_32px_-20px_rgb(16_24_40/0.18)] ' +
  'dark:ring-white/[0.06] dark:shadow-[0_1px_0_rgb(255_255_255/0.03)_inset,0_16px_32px_-18px_rgb(0_0_0/0.8)]';

/**
 * A surface inside a panel: a destination, the latest test result.
 *
 * In the light theme a white panel holding white items has nothing to separate them, so an inner
 * surface steps down to a cool grey with its own hairline; in the dark theme it steps up, a shade
 * lighter than the panel it sits on.
 */
export const innerSurface =
  'rounded-lg bg-slate-50 ring-1 ring-inset ring-slate-900/[0.07] dark:bg-white/[0.025] dark:ring-white/[0.06]';

/** A hairline between rows, neutral in both themes. */
export const hairline = 'border-slate-900/[0.07] dark:border-white/[0.07]';

/** The same hairline, for `divide-y` lists. */
export const divider = 'divide-slate-900/[0.07] dark:divide-white/[0.06]';

/** A quiet filled button, for row actions that should not shout. */
export const quietButton =
  'border-0 bg-slate-900/[0.05] text-foreground shadow-none hover:bg-slate-900/[0.09] dark:bg-white/[0.06] dark:hover:bg-white/[0.1]';

/** Primary buttons keep their colour but lose the glow, which read as a border on this page. */
export const flatPrimary = '!shadow-none';

/** The dialogs opened from this page: the same surface, a deeper shadow, no blue halo. */
export const dialogSurface =
  'rounded-xl border-slate-900/[0.08] !shadow-[0_24px_64px_-16px_rgb(16_24_40/0.3)] dark:border-white/[0.08] dark:!shadow-[0_24px_64px_-12px_rgb(0_0_0/0.7)]';

/**
 * How loudly the health panel speaks, per state.
 *
 * The outline takes the state's colour only when something is wrong, and the status block gets a
 * wash of it, so "At risk" is noticed before it is read. Healthy stays quiet: a green frame around
 * good news would be the page shouting about nothing.
 */
export const urgency: Record<'healthy' | 'attention' | 'risk', { ring: string; block: string; icon: string }> = {
  healthy: {
    ring: '',
    block: 'bg-success/[0.045] dark:bg-success/[0.06]',
    icon: 'bg-success/[0.14] text-success dark:bg-success/[0.15]',
  },
  attention: {
    ring: 'ring-warning/30 dark:ring-warning/25',
    block: 'bg-warning/[0.07] dark:bg-warning/[0.08]',
    icon: 'bg-warning/[0.16] text-warning dark:bg-warning/[0.16]',
  },
  risk: {
    ring: 'ring-danger/30 dark:ring-danger/30',
    block: 'bg-danger/[0.055] dark:bg-danger/[0.09]',
    icon: 'bg-danger/[0.14] text-danger dark:bg-danger/[0.18]',
  },
};
